/* @jsx h */
import { computed, defineComponent, h, ref } from "vue";
import { LemmonModal, useLocalization } from "@lemmonhq/uikit";
import { qrcode } from "@lemmonhq/blogic/lib/receipt";
import { useAuth } from "@app/vue/providers/Auth";

type QrLinkType = "reservations" | "events" | "online-ordering";

export const RestaurantInfoModal = defineComponent<{
  onClose: Function;
}>({
  name: "RestaurantInfoModal",
  props: ["onClose"],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const selectedType = ref<QrLinkType>("reservations");
    const copied = ref(false);

    const restaurant = computed(() => auth.user.value?.restaurant);
    const restaurantUrl = computed(() => {
      const baseUrl = import.meta.env.VITE_CUSTOMER_APP_PUBLIC_URL.replace(
        /\/$/,
        "",
      );

      return `${baseUrl}/restaurant/${restaurant.value?.slug}`;
    });

    const qrLinks = computed(() => [
      {
        type: "reservations" as const,
        title: t("admin.restaurant_qr.reservations_title"),
        description: t("admin.restaurant_qr.reservations_description"),
        icon: "calendar_month",
        url: `${restaurantUrl.value}/reserve/tables`,
      },
      {
        type: "events" as const,
        title: t("admin.restaurant_qr.events_title"),
        description: t("admin.restaurant_qr.events_description"),
        icon: "event",
        url: `${restaurantUrl.value}/reserve/events`,
      },
      {
        type: "online-ordering" as const,
        title: t("admin.restaurant_qr.ordering_title"),
        description: t("admin.restaurant_qr.ordering_description"),
        icon: "takeout_dining",
        url: restaurantUrl.value,
      },
    ]);

    const selectedLink = computed(
      () =>
        qrLinks.value.find((link) => link.type === selectedType.value) ??
        qrLinks.value[0],
    );

    const svgCode = computed(() => {
      const qr = qrcode(6, "L");
      qr.addData(selectedLink.value.url);
      qr.make();

      return qr.createSvgTag({
        cellSize: 8,
        margin: 32,
        scalable: true,
        title: selectedLink.value.title,
        alt: selectedLink.value.url,
      });
    });

    const gifDataUrl = computed(() => {
      const qr = qrcode(6, "L");
      qr.addData(selectedLink.value.url);
      qr.make();

      return qr.createDataURL(15);
    });

    const selectLink = (type: QrLinkType) => {
      selectedType.value = type;
      copied.value = false;
    };

    const downloadQrCode = () => {
      const blob = new Blob([svgCode.value], { type: "image/svg+xml" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${restaurant.value?.slug}-${selectedLink.value.type}-qr.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);
    };

    const downloadQrGif = () => {
      const link = document.createElement("a");
      link.href = gifDataUrl.value;
      link.download = `${restaurant.value?.slug}-${selectedLink.value.type}-qr.gif`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const copyUrl = async () => {
      try {
        await navigator.clipboard.writeText(selectedLink.value.url);
        copied.value = true;
        window.setTimeout(() => (copied.value = false), 1600);
      } catch (error) {
        console.error("Failed to copy restaurant URL:", error);
      }
    };

    return () => (
      <LemmonModal
        title={`${restaurant.value?.name} | ${t("admin.restaurant_qr.title")}`}
        onClose={props.onClose}
      >
        <div class="flex flex-col gap-5 p-4 sm:p-6">
          <div
            role="tablist"
            aria-label={t("admin.restaurant_qr.heading")}
            class="grid grid-cols-3 gap-1 rounded-2xl bg-base-200 p-1.5"
          >
            {qrLinks.value.map((link) => {
              const isSelected = link.type === selectedType.value;

              return (
                <button
                  key={link.type}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  class={[
                    "flex min-w-0 flex-col items-center justify-center gap-1 rounded-xl px-1.5 py-2.5 text-xs font-medium transition sm:flex-row sm:gap-2 sm:px-3 sm:text-sm",
                    isSelected
                      ? "bg-base-100 text-accent shadow-sm"
                      : "text-base-content hover:text-base-content",
                  ]}
                  onClick={() => selectLink(link.type)}
                >
                  <span class="material-symbols-outlined text-xl">
                    {link.icon}
                  </span>
                  <span class="w-full truncate sm:w-auto">{link.title}</span>
                </button>
              );
            })}
          </div>

          <section
            role="tabpanel"
            class="grid gap-5 rounded-2xl border border-base-300 bg-base-100 p-4 sm:grid-cols-[minmax(0,15rem)_1fr] sm:items-center sm:p-5"
          >
            <div class="mx-auto aspect-square w-full max-w-56 overflow-hidden rounded-xl border border-base-300 bg-base-100 p-2 shadow-inner sm:max-w-none">
              <div
                class="h-full w-full *:h-full *:w-full"
                v-html={svgCode.value}
              />
            </div>

            <div class="flex min-w-0 flex-col text-center sm:text-left">
              <div class="flex items-center gap-2">
                <div class="mx-auto flex size-11 items-center justify-center rounded-xl bg-primary/10 text-primary sm:mx-0">
                  <span class="material-symbols-outlined">
                    {selectedLink.value.icon}
                  </span>
                </div>
                <div class="flex-1">
                  <h3 class="mt-3 text-xl font-semibold text-accent">
                    {selectedLink.value.title}
                  </h3>
                  <p class="mt-1 text-sm text-base-content/60">
                    {selectedLink.value.description}
                  </p>
                </div>
              </div>

              <button
                type="button"
                class="btn btn-ghost btn-sm mt-4 h-auto min-h-0 w-full min-w-0 max-w-full flex-nowrap justify-between gap-2 border border-base-300 px-3 py-2 font-mono text-[11px] font-normal normal-case text-base-content/60"
                title={selectedLink.value.url}
                onClick={() => void copyUrl()}
              >
                <span class="min-w-0 flex-1 truncate text-left">
                  {selectedLink.value.url}
                </span>
                <span class="material-symbols-outlined shrink-0 text-lg">
                  {copied.value ? "check" : "content_copy"}
                </span>
              </button>

              <div class="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <button
                  type="button"
                  class="btn btn-primary w-full gap-2"
                  onClick={downloadQrCode}
                >
                  <span class="material-symbols-outlined">code</span>
                  {t("admin.restaurant_qr.download_svg")}
                </button>
                <button
                  type="button"
                  class="btn btn-neutral w-full gap-2"
                  onClick={downloadQrGif}
                >
                  <span class="material-symbols-outlined">image</span>
                  {t("admin.restaurant_qr.download_gif")}
                </button>
              </div>
            </div>
          </section>
        </div>
      </LemmonModal>
    );
  },
});
